import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import type { GeoJSONSource, LayerSpecification, LngLatBoundsLike, Map as MapboxMap, MapMouseEvent, StyleSpecification } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import regionsRaw from './assets/france-regions.geojson?raw';
import { UMR, UM_MAPBOX_STYLES, type UMBasemapId } from './referenceTokens';

type FeatureCollection = GeoJSON.FeatureCollection;

/** Token resolution identical to production: runtime injection, then Vite env. */
export function getMapboxToken(): string {
  const runtime = (window as unknown as { _env_?: Record<string, string> })._env_?.VITE_MAPBOX_TOKEN;
  return runtime || (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) || '';
}

const FRANCE_BOUNDS: LngLatBoundsLike = [[-5.2, 41.3], [9.6, 51.1]];

/** Offline basemap: approximates Mapbox light-v11 (land, water, white boundaries) without any token. */
function offlineStyle(): StyleSpecification {
  return {
    version: 8,
    sources: { 'um-regions': { type: 'geojson', data: JSON.parse(regionsRaw) as FeatureCollection } },
    layers: [
      { id: 'um-water', type: 'background', paint: { 'background-color': UMR.map.fallbackWater } },
      { id: 'um-land', type: 'fill', source: 'um-regions', paint: { 'fill-color': UMR.map.fallbackLand } },
      { id: 'um-boundaries', type: 'line', source: 'um-regions', paint: { 'line-color': UMR.map.fallbackBorder, 'line-width': 1.5 } },
    ],
  };
}

export interface UMMapHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  flyTo: (center: [number, number], zoom?: number) => void;
  fitBounds: (bounds: LngLatBoundsLike, padding?: number) => void;
  getMap: () => MapboxMap | null;
}

export interface UMMapProps {
  basemap?: UMBasemapId;
  /** GeoJSON sources keyed by id; updating the object updates the data in place. */
  sources?: Record<string, FeatureCollection>;
  /** Mapbox layer specs referencing the ids of `sources`. Use UMR.map colours. */
  layers?: LayerSpecification[];
  /** Layers that emit `onFeatureClick` and show a pointer cursor. */
  interactiveLayerIds?: string[];
  onFeatureClick?: (layerId: string, feature: mapboxgl.GeoJSONFeature) => void;
  onMapClick?: () => void;
  initialBounds?: LngLatBoundsLike;
  is3d?: boolean;
  /** Called whenever the active state of the offline fallback changes. */
  onModeChange?: (mode: 'mapbox' | 'offline') => void;
}

/**
 * UrbanMap map surface. Uses the real production Mapbox styles when a token is
 * available, otherwise an offline light basemap so prototypes always run.
 */
export const UMMap = forwardRef<UMMapHandle, UMMapProps>(function UMMap(
  { basemap = 'light', sources = {}, layers = [], interactiveLayerIds = [], onFeatureClick, onMapClick, initialBounds = FRANCE_BOUNDS, is3d = false, onModeChange },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const token = getMapboxToken();
  const mode = token ? 'mapbox' : 'offline';
  const [styleVersion, setStyleVersion] = useState(0);
  const appliedBasemap = useRef(basemap);
  const latest = useRef({ sources, layers, interactiveLayerIds, onFeatureClick, onMapClick });
  latest.current = { sources, layers, interactiveLayerIds, onFeatureClick, onMapClick };

  useImperativeHandle(ref, () => ({
    zoomIn: () => mapRef.current?.zoomIn(),
    zoomOut: () => mapRef.current?.zoomOut(),
    flyTo: (center, zoom) => mapRef.current?.flyTo({ center, zoom: zoom ?? mapRef.current.getZoom() }),
    fitBounds: (bounds, padding = 48) => mapRef.current?.fitBounds(bounds, { padding, maxZoom: 13 }),
    getMap: () => mapRef.current,
  }));

  useEffect(() => {
    if (!containerRef.current) return;
    if (token) mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: token ? UM_MAPBOX_STYLES.find((s) => s.id === basemap)!.style : offlineStyle(),
      bounds: initialBounds,
      attributionControl: true,
      logoPosition: 'bottom-right',
      language: 'fr',
    });
    mapRef.current = map;
    appliedBasemap.current = basemap;
    map.on('style.load', () => setStyleVersion((v) => v + 1));
    map.on('click', (event: MapMouseEvent) => {
      const ids = latest.current.interactiveLayerIds.filter((id) => map.getLayer(id));
      const hit = ids.length ? map.queryRenderedFeatures(event.point, { layers: ids })[0] : undefined;
      if (hit?.layer) latest.current.onFeatureClick?.(hit.layer.id, hit);
      else latest.current.onMapClick?.();
    });
    map.on('mousemove', (event: MapMouseEvent) => {
      const ids = latest.current.interactiveLayerIds.filter((id) => map.getLayer(id));
      const hit = ids.length > 0 && map.queryRenderedFeatures(event.point, { layers: ids }).length > 0;
      map.getCanvas().style.cursor = hit ? 'pointer' : '';
    });
    const resize = new ResizeObserver(() => map.resize());
    resize.observe(containerRef.current);
    return () => {
      resize.disconnect();
      map.remove();
      mapRef.current = null;
    };
    // The map instance is created once; basemap changes go through setStyle below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => onModeChange?.(mode), [mode, onModeChange]);

  useEffect(() => {
    const map = mapRef.current;
    // Re-setting the construction style would wipe the sources/layers added on the first style.load.
    if (!map || !token || appliedBasemap.current === basemap) return;
    appliedBasemap.current = basemap;
    map.setStyle(UM_MAPBOX_STYLES.find((s) => s.id === basemap)!.style);
  }, [basemap, token]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0) return;
    for (const [id, data] of Object.entries(sources)) {
      const existing = map.getSource(id) as GeoJSONSource | undefined;
      if (existing) existing.setData(data);
      else map.addSource(id, { type: 'geojson', data, promoteId: 'id' });
    }
    for (const layer of layers) {
      if (!map.getLayer(layer.id)) map.addLayer(layer);
    }
    for (const layer of layers) {
      if (layer.type === 'line' || layer.type === 'circle' || layer.type === 'fill') {
        for (const [key, value] of Object.entries(layer.paint ?? {})) {
          map.setPaintProperty(layer.id, key as never, value as never);
        }
      }
      if (layer.filter) map.setFilter(layer.id, layer.filter);
    }
  }, [sources, layers, styleVersion]);

  useEffect(() => {
    mapRef.current?.easeTo({ pitch: is3d ? 60 : 0, duration: 600 });
  }, [is3d]);

  return <div ref={containerRef} style={{ position: 'absolute', inset: 0, background: UMR.map.fallbackWater }} />;
});
